import React, { useEffect } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";

import { usePermissions } from "../../login/PermissionContext";
import { useIsAnaesthetist } from "../hooks/useIsAnaesthetist";

/**
 * Gates the PAC Queue route on the anaesthetist roster (GET /ot/me/) instead
 * of the ot_pac permission RestrictedRoute would otherwise check. That
 * permission stopped meaning "anaesthetist" once PAC clearance moved to an
 * OT-owned roster (main/ot/sql/036_ot_anaesthetist.sql) -- see
 * useIsAnaesthetist for why. Mirrors RestrictedRoute's own shape (the
 * super-admin bypass, the redirect to AccessDeniedPage, remembering the last
 * accessible path) so this route behaves the same as every other one in
 * App.js, just sourced from the roster answer rather than the static
 * permission list.
 */
export default function OtPacRoute({ children, ...rest }) {
  const { userInfo, loading: permissionsLoading } = usePermissions();
  const { isAnaesthetist, loading: rosterLoading } = useIsAnaesthetist();
  const location = useLocation();
  const isSuperAdmin = userInfo?.role_permission?.[0]?.role === "super admin";
  const loading = permissionsLoading || rosterLoading;
  const hasAccess = isSuperAdmin || isAnaesthetist;

  useEffect(() => {
    if (!loading && hasAccess) {
      sessionStorage.setItem(
        "lastAccessiblePath",
        `${location.pathname}${location.search || ""}`,
      );
    }
  }, [loading, hasAccess, location.pathname, location.search]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Route
      {...rest}
      render={() =>
        hasAccess ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/AccessDeniedPage",
              state: {
                deniedPath: `${location.pathname}${location.search || ""}`,
                fallbackPath:
                  sessionStorage.getItem("lastAccessiblePath") || "/login",
              },
            }}
          />
        )
      }
    />
  );
}
